/**
 * Görünüm tercihleri — ızgara/liste ve kart boyutu.
 *
 * ## Neden kullanıcı seçebilsin?
 *
 * İki içerik tipi birbirine uymuyor:
 *
 *   - **Film/dizi** → kapak görseli taşıyan az sayıda öğe. Büyük kart iyi;
 *     ama 400 bölümlük bir dizide 190 px kartlar ekrana sığmıyor, sayfalama
 *     zorunlu hale geliyor.
 *   - **Müzik** → uzun parça listeleri. Kapak zaten küçük ve tekrar ediyor;
 *     ızgara yerine satır çok daha hızlı taranır.
 *
 * Tek bir varsayılan ikisine de hizmet etmiyordu. Kullanıcı seçtiğinde
 * tercih `localStorage`'da saklanır ve tüm ekranlarda geçerlidir.
 *
 * ## Neden ayrı anahtar?
 *
 * `useSubtitlePrefs`'in kendi sürümlü anahtarı var (`pbfork.subtitlePrefs.v2`).
 * Görünüm tercihi farklı bir alan; anahtarı ayırmak, altyazı ölçeği yeniden
 * değiştiğinde görünüm tercihinin yanlışlıkla sıfırlanmasını engelliyor.
 */
import { useCallback, useMemo, useState } from 'react';
import { recordSwallowed } from './swallowed.ts';

/** Işaretleme biçimi. */
export type ViewMode = 'grid' | 'list';

/** Kart yoğunluğu. */
export type CardSize = 'sm' | 'md' | 'lg';

export interface ViewPrefs {
  mode: ViewMode;
  size: CardSize;
}

export const DEFAULT_VIEW_PREFS: ViewPrefs = { mode: 'grid', size: 'md' };

/**
 * Boyut seçicisindeki görünen adlar (ikon değil, kısa metin).
 *
 * ## Piksel değerleri nerede?
 *
 * Sütun genişlikleri (`sm` 150 / `md` 190 / `lg` 260 px) **CSS'te** duruyor
 * (`.grid.view-sm|md|lg` → `--grid-cols`). Burada ikinci bir kopya tutulsaydı
 * değerler zamanla ayrışır ve hangisinin doğru olduğu anlaşılamazdı; hangisi
 * değiştirilirse diğeri sessizce yanlış kalırdı. Bu modül tercih **durumunu**
 * yönetir, ölçüleri değil.
 */
export const SIZE_LABELS: Record<CardSize, string> = {
  sm: 'Küçük',
  md: 'Orta',
  lg: 'Büyük',
};

export const MODE_LABELS: Record<ViewMode, string> = {
  grid: 'Izgara',
  list: 'Liste',
};

const STORAGE_KEY = 'pbfork.viewPrefs.v1';

/** Saklanan değerler geçerli mi? Geçersizse varsayılana düşer. */
export function sanitizeViewPrefs(input: Partial<ViewPrefs>): ViewPrefs {
  return {
    mode: input.mode === 'list' || input.mode === 'grid' ? input.mode : DEFAULT_VIEW_PREFS.mode,
    size:
      input.size === 'sm' || input.size === 'md' || input.size === 'lg'
        ? input.size
        : DEFAULT_VIEW_PREFS.size,
  };
}

function readStored(): Partial<ViewPrefs> {
  if (typeof localStorage === 'undefined') return {};
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw === null) return {};
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return {};
    return parsed as Partial<ViewPrefs>;
  } catch (error) {
    recordSwallowed('viewPrefs', error);
    return {};
  }
}

export interface ViewPrefsApi {
  prefs: ViewPrefs;
  setMode: (mode: ViewMode) => void;
  setSize: (size: CardSize) => void;
  /** Izgara ⇄ liste arasında geçiş (tek düğme için). */
  toggleMode: () => void;
  /**
   * Konteynere uygulanacak sınıflar (`view-grid|view-list` + `view-sm|md|lg`).
   *
   * Sınıflar CSS tarafından tanımlıdır; burada yalnızca tercih durumundan
   * üretilir. Bileşenler bu tek dizeyi `<div className={`grid ${…}`}>` olarak
   * kullanır.
   */
  gridClassName: string;
  /** Liste modunda kart satırı mı? */
  isList: boolean;
}

export function useViewPrefs(): ViewPrefsApi {
  const [prefs, setPrefs] = useState<ViewPrefs>(() => ({
    ...DEFAULT_VIEW_PREFS,
    ...sanitizeViewPrefs(readStored()),
  }));

  const update = useCallback((patch: Partial<ViewPrefs>) => {
    setPrefs((previous) => {
      const next = sanitizeViewPrefs({ ...previous, ...patch });
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      } catch (error) {
        recordSwallowed('viewPrefs', error);
        // Depolama kapalı; tercih yine de bu oturumda geçerli.
      }
      return next;
    });
  }, []);

  const isList = prefs.mode === 'list';
  const gridClassName = useMemo(
    () => `view-${prefs.mode} view-${prefs.size}`,
    [prefs.mode, prefs.size],
  );

  return {
    prefs,
    setMode: (mode) => update({ mode }),
    setSize: (size) => update({ size }),
    toggleMode: () => update({ mode: isList ? 'grid' : 'list' }),
    gridClassName,
    isList,
  };
}
